import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axios';
import randomstring from "randomstring";

export const usePdfUpload = (formData, setFormData, errors, setErrors) => {
    const [isMerging, setIsMerging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [mergedPdfUrl, setMergedPdfUrl] = useState(null);
    const [mergedPdfName, setMergedPdfName] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfError, setPdfError] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [randID, setRandID] = useState(randomstring.generate(20));

    async function handleMergePdfs(files, processStreetUrl = null) {
        if (!randID) {
            setRandID(randomstring.generate(20));
        }

        // If only Process Street URL provided (no files)
        if ((!files || files.length === 0) && !processStreetUrl) {
            setMergedPdfUrl(null);
            setMergedPdfName(null);
            return;
        }

        setIsMerging(true);
        setUploadProgress(0);
        try {
            // Create FormData and append files
            const uploadData = new FormData();

            if (files && files.length > 0) {
                files.forEach((file) => {
                    uploadData.append('files', file);
                });
            }

            uploadData.append('id', randID);

            // If Process Street URL provided, add it to formData
            if (processStreetUrl) {
                uploadData.append('processStreetUrl', processStreetUrl);
            }

            // Get the JWT token
            const token = localStorage.getItem('jwtToken');

            // Call merge-pdfs API endpoint
            const response = await api.post('/merge-pdfs', uploadData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000,
                onUploadProgress: (event) => {
                    if (event.total) {
                        const percent = Math.round((event.loaded * 100) / event.total);
                        setUploadProgress(percent);
                    }
                }
            });

            if (response.data && response.data.fileUrl) {
                setMergedPdfUrl(response.data.fileUrl);
                setMergedPdfName(response.data.fileName);

                // If Process Street URL was used, fetch the merged file and add to formData.files
                if (processStreetUrl) {
                    const fileResponse = await fetch(response.data.fileUrl);
                    const blob = await fileResponse.blob();
                    const fileName = processStreetUrl.split('/').pop() || 'meeting_minutes.pdf';
                    const file = new File([blob], fileName, { type: 'application/pdf' });

                    setFormData(prevData => ({
                        ...prevData,
                        files: [file]
                    }));
                }
            }

        } catch (error) {
            console.error('Error merging PDFs:', error);
            toast.error(error?.response?.data?.error || 'Failed to merge PDFs');
            setIsMerging(false);
        } finally {
            setTimeout(() => {
                setIsMerging(false);
                setUploadProgress(100);
            }, 1000);
        }
    }

    // Watch for changes in formData.files
    useEffect(() => {
        if (formData.files && formData.files.length > 0) {
            handleMergePdfs(formData.files);
        } else {
            setIsMerging(false);
            setMergedPdfUrl(null);
            setMergedPdfName(null);
        }
    }, [formData.files]);

    useEffect(() => {
        if (!isMerging && mergedPdfUrl) {
            setErrors(prev => ({ ...prev, mergedPDF: false }));
        }
    }, [isMerging, mergedPdfUrl, setErrors]);

    // Cleanup merged PDF URL
    useEffect(() => {
        return () => {
            if (mergedPdfUrl) {
                URL.revokeObjectURL(mergedPdfUrl);
            }
        };
    }, [mergedPdfUrl]);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
        setPdfError(false);
        if (swiperInstance && numPages > 1) {
            swiperInstance.update();
        }
    }

    function onDocumentLoadError(error) {
        console.error('Error loading PDF:', error);
        setPdfError(true);
    }

    function goToNextPage() {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    }

    function goToPrevPage() {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    }

    useEffect(() => {
        if (swiperInstance) {
            swiperInstance.update();
        }
    }, [mergedPdfUrl, swiperInstance]);

    const resetPdfState = () => {
        setMergedPdfUrl(null);
        setMergedPdfName(null);
        setRandID(randomstring.generate(20));
    };

    return {
        isMerging,
        setIsMerging,
        uploadProgress,
        mergedPdfUrl,
        setMergedPdfUrl,
        mergedPdfName,
        setMergedPdfName,
        numPages,
        pageNumber,
        pdfError,
        swiperInstance,
        setSwiperInstance,
        handleMergePdfs,
        onDocumentLoadSuccess,
        onDocumentLoadError,
        goToNextPage,
        goToPrevPage,
        resetPdfState
    };
};
